import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { VehiclePriceFormView } from './VehiclePriceFormView';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'routes';
import { useDeposit } from 'providers';
import { ClassifiedsService, Tracking } from 'api';
import { ApiError, BadgesResponse } from 'types';
import { Loader } from 'components';
import { useReturn, useBadgesRanges } from 'hooks';
import { Vehicle } from '@groupelacentrale/classified-core';
import { validateVehiclePrice } from 'utils';
import { RANGE_POSITIONS } from 'utils/vehiclePrice';

interface Props {
  defaultErrorMessage?: string;
  isPro: boolean;
}

export const VehiclePriceForm: React.FC<Props> = ({ defaultErrorMessage, isPro }) => {
  const history = useHistory();
  const deposit = useDeposit();
  const { vehicle, depositType, zipCode } = deposit;
  const [badges, setBadges] = useState<BadgesResponse | Record<string, never>>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!badges) {
      ClassifiedsService.getBadges(vehicle as Vehicle, depositType!, zipCode!).then(setBadges);
    }
  }, [vehicle, depositType, zipCode, badges]);

  const refinedQuotation = badges?.refinedQuotation;

  const { values, touched, handleSubmit, isSubmitting, errors, setFieldValue } = useFormik({
    enableReinitialize: true,
    initialValues: {
      vehiclePrice: deposit.price,
    },
    onSubmit: async ({ vehiclePrice }, { setErrors, setSubmitting }) => {
      const errors = validateVehiclePrice(vehiclePrice);

      if (Object.keys(errors).length !== 0) {
        setErrors(errors);
        setSubmitting(false);
        Tracking.sendCurrentPageError();
        return;
      }

      await new Promise<void>(async (resolve, reject) => {
        try {
          await deposit.setPrice(vehiclePrice!);
          if (isPro) {
            const classified = await deposit.publishClassified();
            if (classified) history.push(ROUTES.confirmation);
          } else {
            history.push(ROUTES.publicationOptions);
          }
          resolve();
        } catch (e) {
          const error = e as ApiError;
          error.message && setErrorMessage(error.message);
          Tracking.sendCurrentPageError();
          setSubmitting(false);
          reject();
        }
      });
    },
  });

  const badgesRanges = useBadgesRanges(refinedQuotation);

  const onReturn = useReturn(ROUTES.contactInformation);

  if (badges === undefined) {
    return <Loader />;
  } else {
    return (
      <VehiclePriceFormView
        isPro={isPro}
        positions={RANGE_POSITIONS}
        badgesRanges={badgesRanges}
        refinedQuotation={refinedQuotation}
        vehiclePrice={values.vehiclePrice}
        isSubmitting={isSubmitting}
        errors={errors}
        touched={touched}
        errorMessage={defaultErrorMessage || errorMessage}
        handleSubmit={handleSubmit}
        onReturn={onReturn}
        setFieldValue={setFieldValue}
      />
    );
  }
};
